'use client';

import { useCallback, useState } from 'react';

import { useAuiState } from '@assistant-ui/react';
import { PanelLeftIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useTimeoutFn, useUpdateEffect } from 'react-use';

import { useReadOnly } from '@/components/providers/read-only-provider';
import { Button } from '@/components/ui/button';
import { useSidebar } from '@/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { readJsonFromLocalStorage, writeJsonToLocalStorage } from '@/lib/storage/safe-storage';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'sidebar-tooltip';
const MAX_SHOWS = 2;
const TTL_MS = 60 * 60 * 1000 * 3;

interface StorageData {
  count: number;
  expiresAt: number;
}

function getStorageData(): StorageData | null {
  const data = readJsonFromLocalStorage<StorageData | null>({ key: STORAGE_KEY, fallback: null });
  if (!data || Date.now() > data.expiresAt) return null;
  return data;
}

function incrementShowCount(): void {
  const existing = getStorageData();
  writeJsonToLocalStorage<StorageData>({
    key: STORAGE_KEY,
    value: {
      count: (existing?.count ?? 0) + 1,
      expiresAt: existing?.expiresAt ?? Date.now() + TTL_MS,
    },
  });
}

function canShowTooltip(): boolean {
  const data = getStorageData();
  return !data || data.count < MAX_SHOWS;
}

type SidebarTriggerProps = React.ComponentProps<typeof Button>;

export function SidebarTrigger({ className, onClick, ...props }: SidebarTriggerProps) {
  const t = useTranslations('sidebar');
  const { isMobile, forceOverlay, toggleSidebar } = useSidebar();
  const threadItems = useAuiState(state => state.threads.threadItems);
  const isReadOnly = useReadOnly();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [hasShownTooltip, setHasShownTooltip] = useState(false);

  const shouldShowTooltip = !isReadOnly && (isMobile || forceOverlay);
  const hasExistingThreads = threadItems.length > 1;

  useTimeoutFn(() => setTooltipOpen(false), tooltipOpen ? 2500 : undefined);

  useUpdateEffect(() => {
    if (hasExistingThreads && !hasShownTooltip && shouldShowTooltip && canShowTooltip()) {
      setHasShownTooltip(true);
      setTooltipOpen(true);
      incrementShowCount();
    }
  }, [hasExistingThreads, shouldShowTooltip]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      toggleSidebar();
    },
    [onClick, toggleSidebar],
  );

  const triggerButton = (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="ghost"
      size="icon"
      className={cn('size-7', className)}
      onClick={handleClick}
      {...props}
    >
      <PanelLeftIcon />
      <span className="sr-only">{t('toggleTooltip')}</span>
    </Button>
  );

  if (isReadOnly) {
    return triggerButton;
  }

  return (
    <Tooltip open={tooltipOpen}>
      <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
      <TooltipContent side="bottom">{t('toggleTooltip')}</TooltipContent>
    </Tooltip>
  );
}
